import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { commandHeader, envelope } from '../../../shared/api/envelope'
import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import type { CommandIntent } from '../../../shared/model/ids'
import { AQSI_SALE_URLS } from '../../sale/api/urls'
import type { AqsiRefund, SeatActive } from '../model/types'
import type { SlipRefundSale, SlipRefundSeat, SlipRefundSlips } from '../model/slipRefundTypes'
import { decodeRefund, decodeSeatActive } from './contracts'
import { decodeSlipRefundSale, decodeSlipRefundSeat, decodeSlipRefundSlips } from './slipContracts'
import { AQSI_REFUND_URLS } from './urls'

/** Касса работы: `registerKey` — ключ кассы из ответа службы (ключ кэша), `deviceId` — касса кабинета (RP-C9). */
export interface RefundSeatArgs {
  resourceId: string
  registerKey: string
  deviceId: number
}

export interface RefundArgs {
  resourceId: string
  refundId: string
}

/** Позиция запроса: номер позиции исходного чека и количество. Цену, ставку и способ возврата браузер не присылает (C4b — 422). */
export interface RefundLineRequest {
  ordinal: number
  quantity: number
}

export interface RefundCreateArgs extends RefundSeatArgs {
  receiptId: string
  lines: readonly RefundLineRequest[]
  intent: CommandIntent
  /** Версия МОЕЙ сессии за кассой (как у создания продажи). */
  expectedVersion: number
}

export interface RefundCommandArgs extends RefundSeatArgs {
  refundId: string
  intent: CommandIntent
  /** Версия возврата, которую видел человек. */
  expectedVersion: number
}

/** Слипы покупки кассы для возврата по слипу (RP-FB74). */
export interface SlipRefundSlipsArgs {
  resourceId: string
  deviceId: number
}

/**
 * Возврат по слипу БЕЗ чека (RP-FB74): слип, выбранный из списка, и сумма в копейках. Одно нажатие человека - одно намерение (ключ
 * идемпотентности); `expectedVersion` - версия МОЕЙ сессии за кассой.
 */
export interface SlipRefundSendArgs extends RefundSeatArgs {
  originalSlipId: string
  amountKopecks: number
  intent: CommandIntent
  expectedVersion: number
}

/** Что перечитать, когда касса места могла измениться, а ответа нет или он — отказ. */
const seatTags = (resourceId: string, registerKey: string) => [resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey)]

const decodeRefundReply = (resourceId: string) => envelope('aqsi.sale', resourceId, (raw) => decodeRefund(raw))

/**
 * Ручки возврата C4b в ОБЩЕМ `baseApi`: имена `aqsi*`, rid в аргументах и
 * тегах, `resourceScopeTag(rid)` у каждого чтения. Деньги, чек и отмена
 * возврата — ручки продажи C3b с декодером ВОЗВРАТА. Transport команды не
 * повторяет (ADR-11): без ответа окно ЧИТАЕТ кассу, а не шлёт POST ещё раз.
 */
export const refundsApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    /**
     * Что на кассе места: возврат, продажа или ничего. Тот же адрес, что у окна
     * продажи, но своя запись кэша (свой декодер) и тот же тег кассы — команда
     * любого окна перечитывает оба.
     */
    aqsiRefundSeat: build.query<SeatActive, RefundSeatArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.active(resourceId),
        params: { deviceId },
        decode: envelope('aqsi.sale', resourceId, decodeSeatActive),
      }),
      providesTags: (_result, _error, { resourceId, registerKey }) => [
        resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
        resourceListTag(AQSI_TAGS.saleActive, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Слипы покупки кассы: сумма, время, сколько уже возвращено нашими возвратами (RP-FB74). Только чтение. */
    aqsiSlipRefundSlips: build.query<SlipRefundSlips, SlipRefundSlipsArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_REFUND_URLS.slipRefundSlips(resourceId),
        params: { deviceId },
        decode: envelope('aqsi.slip-refund-slips', resourceId, decodeSlipRefundSlips),
      }),
      providesTags: (_result, _error, { resourceId, deviceId }) => [
        resourceTag(AQSI_TAGS.refund, resourceId, `slips:${deviceId}`),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Что на кассе места глазами окна возврата по слипу: свободна, открытый возврат по слипу или другая операция. Только чтение. */
    aqsiSlipRefundSeat: build.query<SlipRefundSeat, RefundSeatArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.active(resourceId),
        params: { deviceId },
        decode: envelope('aqsi.sale', resourceId, decodeSlipRefundSeat),
      }),
      providesTags: (_result, _error, { resourceId, registerKey }) => [
        resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
        resourceListTag(AQSI_TAGS.saleActive, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * Вернуть деньги по слипу без чека: служба создаёт возврат и ОДИН раз отправляет его в кабинет. Transport команду не повторяет
     * (ADR-11): без ответа окно ЧИТАЕТ кассу и слипы, а не шлёт POST ещё раз. После любого исхода касса и остаток слипа перечитываются.
     */
    aqsiSlipRefundSend: build.mutation<SlipRefundSale, SlipRefundSendArgs>({
      query: ({ resourceId, deviceId, originalSlipId, amountKopecks, intent, expectedVersion }) => ({
        url: AQSI_REFUND_URLS.slipRefund(resourceId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.refund.slip', resourceId, intent, expectedVersion), deviceId, originalSlipId, amountKopecks },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSlipRefundSale(raw)),
        timeoutMs: 90_000,
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey, deviceId }) => [
        ...seatTags(resourceId, registerKey),
        resourceTag(AQSI_TAGS.refund, resourceId, `slips:${deviceId}`),
      ],
    }),
    /** Возврат по id — для итога завершённого возврата. */
    aqsiRefund: build.query<AqsiRefund, RefundArgs>({
      query: ({ resourceId, refundId }) => ({
        url: AQSI_SALE_URLS.sale(resourceId, refundId),
        decode: decodeRefundReply(resourceId),
      }),
      providesTags: (_result, _error, { resourceId, refundId }) => [
        resourceTag(AQSI_TAGS.refund, resourceId, refundId),
        resourceScopeTag(resourceId),
      ],
    }),

    /** Возврат по чеку журнала ЭТОГО rid: позиции, цена, ставка и способ — из чека, решает служба. */
    aqsiRefundCreate: build.mutation<AqsiRefund, RefundCreateArgs>({
      query: ({ resourceId, deviceId, receiptId, lines, intent, expectedVersion }) => ({
        url: AQSI_REFUND_URLS.refunds(resourceId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader('aqsi.refund.create', resourceId, intent, expectedVersion),
          deviceId,
          receiptId,
          lines,
        },
        decode: decodeRefundReply(resourceId),
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? seatTags(resourceId, registerKey) : [],
    }),
    /** Вернуть деньги: карта — один возврат на терминале по слипу исходной оплаты, наличные — сразу чек. */
    aqsiRefundPay: build.mutation<AqsiRefund, RefundCommandArgs>({
      query: ({ resourceId, deviceId, refundId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.pay(resourceId, refundId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.pay', resourceId, intent, expectedVersion), saleId: refundId, deviceId },
        decode: decodeRefundReply(resourceId),
        timeoutMs: 90_000,
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? seatTags(resourceId, registerKey) : [],
    }),
    /** Чек возврата прихода — после денег (у карты) или после доказанного отказа чека. */
    aqsiRefundFiscalize: build.mutation<AqsiRefund, RefundCommandArgs>({
      query: ({ resourceId, deviceId, refundId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.fiscalize(resourceId, refundId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.fiscalize', resourceId, intent, expectedVersion), saleId: refundId, deviceId },
        decode: decodeRefundReply(resourceId),
        timeoutMs: 90_000,
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? seatTags(resourceId, registerKey) : [],
    }),
    /** Отмена возврата, по которому деньги не двигались и чек не пробит: освобождает количество чека. */
    aqsiRefundCancel: build.mutation<AqsiRefund, RefundCommandArgs>({
      query: ({ resourceId, deviceId, refundId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.cancel(resourceId, refundId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.cancel', resourceId, intent, expectedVersion), saleId: refundId, deviceId },
        decode: decodeRefundReply(resourceId),
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? seatTags(resourceId, registerKey) : [],
    }),
  }),
})

export const {
  aqsiRefundSeat,
  aqsiRefund,
  aqsiRefundCreate,
  aqsiRefundPay,
  aqsiRefundFiscalize,
  aqsiRefundCancel,
  aqsiSlipRefundSlips,
  aqsiSlipRefundSeat,
  aqsiSlipRefundSend,
} = refundsApi.endpoints
